import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { useLearningBudgetSetupMutation } from '@/modules/common/hooks/data-hooks/useLearningBudgetSetupMutation';
import { useLearningBudgetSetupUpdateMutation } from '@/modules/common/hooks/data-hooks/useLearningBudgetSetupUpdateMutation';
import { useLearningBudgetSetups } from '@/modules/common/hooks/data-hooks/useLearningBudgetSetups';
import { getAbbreviatedUUID } from '@/modules/common/lib/id';
import { ListLearningBudgetSetupsTable } from '@/modules/learning-budgets/components/LearningBudgetSetupsTable';
import { CompanyLevelLearningBudgetModal } from '@/modules/learning-budgets/components/LearningBudgetSetupsTable/modals/CompanyLevelLearningBudgetModal';
import { CompanySettingsSearchParams } from '@/routes/_admin/settings';
import { ApiError } from '@/services/api';
import {
  ILearningBudgetSetupInTheList,
  LearningBudgetSetupStatus,
} from '@/types/learning-budget';
import { UserRole } from '@/types/user';
import { Divider } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useCompanyMutation } from '../hooks/useCompanyMutation';
import {
  LearningBudgetForm,
  LearningBudgetFormData,
} from './LearningBudgetForm';

export function LearningBudgetTab() {
  const [isCreatingNewCycle, setIsCreatingNewCycle] = useState(false);

  const search = useSearch({
    strict: false,
  }) as CompanySettingsSearchParams;
  const navigate = useNavigate();

  const {
    employer,
    isLoading: isLoadingEmployer,
    currentUser,
  } = useCurrentUserContext() ?? {};

  const { mutateAsync: updateCompany } = useCompanyMutation(
    employer?._id ?? '',
  );
  const { mutateAsync: createLearningBudgetSetup } =
    useLearningBudgetSetupMutation();
  const { mutateAsync: updateLearningBudgetSetup } =
    useLearningBudgetSetupUpdateMutation();

  const {
    data: {
      data: learningBudgetSetups = [],
      metadata: { total = 0, page = 1 } = {},
    } = {},
    isLoading: isLoadingLearningBudgetSetups,
    isRefetching: isRefetchingLearningBudgetSetups,
    refetch: refetchLearningBudgetSetups,
  } = useLearningBudgetSetups({
    filters: {
      company: employer?._id ?? '',
    },
    pagination: {
      page: search.page || 1,
      limit: search.limit || 10,
    },
    sort: {
      createdAt: 'desc',
    },
    options: {
      // TODO: preserve draft state of not changed cycles
      placeholderData: keepPreviousData,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  const isEmployerUser = currentUser?.roles.includes(UserRole.Employer);
  const paginationState = {
    pageIndex: (page ?? 1) - 1 || 0,
    pageSize: search.limit || 10,
  };

  const handleSubmit = async (updatedData: LearningBudgetFormData) => {
    try {
      await updateCompany({
        updatedData,
      });
      enqueueSnackbar('Learning budget settings updated successfully', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to update learning budget settings', {
        variant: 'error',
      });
      throw new Error('Failed to update learning budget settings');
    }
  };

  const handleSetupUpdate = async (data: ILearningBudgetSetupInTheList) => {
    try {
      const updateRequest = {
        id: data._id,
        update: {
          status: data.status,
          startDate: data.startDate,
          defaultBudget: data.defaultBudget,
          carryOver: data.carryOver,
          teamOverrides: data.teamOverrides.map(override => ({
            team: override.team._id,
            budget: override.budget,
          })),
          employeeOverrides: data.employeeOverrides.map(override => ({
            employee: override.employee._id,
            budget: override.budget,
          })),
        },
      };
      await updateLearningBudgetSetup(updateRequest);
      enqueueSnackbar(
        `Learning budget setup ${getAbbreviatedUUID(data._id)} updated successfully`,
        {
          variant: 'success',
        },
      );
      await refetchLearningBudgetSetups();
    } catch {
      enqueueSnackbar(
        `Failed to update learning budget setup ${getAbbreviatedUUID(
          data._id,
        )}`,
        {
          variant: 'error',
        },
      );
    }
  };

  const onPaginationChange = ({ pageIndex, pageSize }: PaginationState) => {
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: pageIndex + 1,
        limit: pageSize,
      }),
    });
  };

  const isLoading =
    isLoadingEmployer ||
    isLoadingLearningBudgetSetups ||
    isRefetchingLearningBudgetSetups;

  if (!isEmployerUser) {
    return null;
  }

  return (
    <>
      <LearningBudgetForm
        className="mt-8"
        initialData={{
          learningBudget: employer?.learningBudget ?? false,
        }}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        onCreateNewCycle={async () => {
          setIsCreatingNewCycle(true);
        }}
      />
      <Divider className="my-8" />
      <div className="h-full">
        <ListLearningBudgetSetupsTable
          tableOptions={{
            data: learningBudgetSetups,
            state: {
              isLoading,
              pagination: paginationState,
            },
            rowCount: total,
            onPaginationChange: updater => {
              return typeof updater === 'function'
                ? onPaginationChange(updater(paginationState))
                : onPaginationChange(updater);
            },
          }}
          onSetupUpdate={handleSetupUpdate}
        />
      </div>
      <CompanyLevelLearningBudgetModal
        open={isCreatingNewCycle}
        onClose={async values => {
          if (values) {
            try {
              await createLearningBudgetSetup({
                status: LearningBudgetSetupStatus.Draft,
                startDate: values.startDate.toISOString(),
                defaultBudget: values.amount,
              });
              enqueueSnackbar('Learning budget setup created successfully', {
                variant: 'success',
              });
              await refetchLearningBudgetSetups();
            } catch (e) {
              if (e instanceof ApiError && e.statusCode === 409) {
                enqueueSnackbar(
                  'A learning budget setup already exists for this period',
                  {
                    variant: 'error',
                  },
                );
              } else {
                enqueueSnackbar('Failed to create learning budget setup', {
                  variant: 'error',
                });
              }
            }
          }

          setIsCreatingNewCycle(false);
        }}
      />
    </>
  );
}
